import { useMemo, useState } from 'react';
import { useSyncStore } from '../../sync/model/sync.store';
import { useTasksData, refreshTasks } from '../model/tasks.store';
import { useTaskUiStore } from '../model/tasks.ui';
import {
  selectListKindTasks,
  listTagOptions,
  listProjectOptions,
  filterTaskListItems,
  hasActiveFilters,
} from '../model/selectors';
import type { TaskListKind } from '../model/task.types';
import { EMPTY_TASK_FILTERS, type TaskFilters } from '../model/task.types';
import { useUserDates } from '../lib/useUserDates';
import { useTaskActions } from '../lib/useTaskActions';
import { useIsMobileViewport } from '../../../shared/lib/useMediaQuery';
import { EmptyState, ListSkeleton } from '../../../shared/ui/states';
import { Button } from '../../../shared/ui/Button';
import { ListScreenShell } from './ListScreenShell';
import { TaskFiltersBar } from './TaskFiltersBar';
import { TaskRow } from './TaskRow';
import { ConfirmDialog } from './ConfirmDialog';
import { showToast } from '../../../shared/ui/toast';

function useOfflineFlag(): { offline: boolean; pendingCount: number } {
  const online = useSyncStore((state) => state.online);
  const pendingCount = useSyncStore((state) => state.pendingCount);
  return { offline: !online, pendingCount };
}

interface TaskListScreenProps {
  kind: Exclude<TaskListKind, 'overdue' | 'archive'>;
}

const SCREEN_META: Record<
  Exclude<TaskListKind, 'overdue' | 'archive'>,
  { title: string; description: string; emptyTitle: string; emptyDescription: string }
> = {
  today: {
    title: 'Сегодня',
    description: 'Просроченное, задачи на сегодня и блок «Без даты»',
    emptyTitle: 'На сегодня задач нет',
    emptyDescription: 'Добавьте задачу на сегодня или выполните просроченные.',
  },
  tomorrow: {
    title: 'Завтра',
    description: 'Задачи на следующий день',
    emptyTitle: 'На завтра задач нет',
    emptyDescription: 'Добавьте задачу на завтра — она появится здесь.',
  },
};

function TaskListScreen({ kind }: TaskListScreenProps) {
  const data = useTasksData();
  const { offline, pendingCount } = useOfflineFlag();
  const { today, tomorrow } = useUserDates();
  const openTaskEditor = useTaskUiStore((state) => state.openTaskEditor);
  const actions = useTaskActions();
  const loading = data.loading;
  const meta = SCREEN_META[kind];

  const groups = useMemo(
    () => selectListKindTasks(data, kind, today, tomorrow),
    [data, kind, today, tomorrow],
  );

  const tags = useMemo(() => listTagOptions(data), [data]);
  const projects = useMemo(() => listProjectOptions(data), [data]);

  const quickAddDate = kind === 'today' ? 'today' : 'tomorrow';

  return (
    <ListScreenShell
      title={meta.title}
      description={meta.description}
      loading={loading}
      error={data.error}
      offline={offline}
      pendingCount={pendingCount}
      items={groups.list}
      overdue={groups.overdue}
      undated={groups.undated}
      allTags={tags}
      allProjects={projects}
      quickAddDate={quickAddDate}
      emptyTitle={meta.emptyTitle}
      emptyDescription={meta.emptyDescription}
      onOpenEditor={(taskId) => {
        openTaskEditor(taskId);
      }}
      onRetry={() => {
        void refreshTasks();
      }}
      onCreateTag={(name, color) => actions.createTag(name, color)}
    />
  );
}

export function TodayScreen() {
  return <TaskListScreen kind="today" />;
}

export function TomorrowScreen() {
  return <TaskListScreen kind="tomorrow" />;
}

/**
 * Архив: мультивыбор, восстановление, удаление навсегда.
 * Сортировка: невыполненные сверху, выполненные внизу.
 * Restore: due_date серии не сдвигается (отображение from_date=today).
 */
export function ArchiveScreen() {
  const data = useTasksData();
  const { offline, pendingCount } = useOfflineFlag();
  const { today } = useUserDates();
  const openTaskEditor = useTaskUiStore((state) => state.openTaskEditor);
  const actions = useTaskActions();
  const isDesktop = !useIsMobileViewport();
  const loading = data.loading;
  const tags = useMemo(() => listTagOptions(data), [data]);
  const projects = useMemo(() => listProjectOptions(data), [data]);

  const [filters, setFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteOne, setDeleteOne] = useState<{ id: string; title: string } | null>(null);

  const groups = useMemo(
    () => selectListKindTasks(data, 'archive', today, today),
    [data, today],
  );
  const items = useMemo(
    () => filterTaskListItems(groups.list, filters),
    [groups.list, filters],
  );

  function toggleSelect(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function clearSelection(): void {
    setSelectedIds(new Set());
    setSelectionMode(false);
  }

  async function handleRestore(ids: string[]): Promise<void> {
    let restored = 0;
    for (const id of ids) {
      const item = data.tasks.find((task) => task.id === id);
      if (item === undefined) {
        continue;
      }
      // Одна тост-итерация: bulk-восстановление без спама уведомлений
      const ok = await actions.restoreTaskSilently(item);
      if (ok) {
        restored += 1;
      }
    }
    await refreshTasks();
    if (restored > 0) {
      showToast(
        restored === 1
          ? 'Задача восстановлена'
          : `Восстановлено задач: ${restored}`,
        'success',
      );
    }
    clearSelection();
  }

  async function handleDeleteForever(ids: string[]): Promise<void> {
    for (const id of ids) {
      await actions.deleteTaskForeverSilently(id);
    }
    await refreshTasks();
    showToast(
      ids.length === 1 ? 'Задача удалена навсегда' : `Удалено задач: ${ids.length}`,
      'success',
    );
    setDeleteConfirmOpen(false);
    setDeleteOne(null);
    clearSelection();
  }

  const selectedArray = [...selectedIds];

  return (
    <div className="tg-page space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-tg-text">Архив</h1>
          <p className="mt-1 text-sm text-tg-text-secondary">
            Архивные задачи: восстановить или удалить навсегда
          </p>
        </div>
        <Button
          type="button"
          variant={selectionMode ? 'secondary' : 'ghost'}
          onClick={() => {
            if (selectionMode) {
              clearSelection();
            } else {
              setSelectionMode(true);
            }
          }}
        >
          {selectionMode ? 'Отменить выбор' : 'Выбрать'}
        </Button>
      </header>

      {offline ? (
        <div className="rounded-2xl border border-tg-warning/40 bg-tg-surface px-4 py-3 text-sm text-tg-text-secondary">
          Вы офлайн. Изменения архива сохранятся на устройстве
          {pendingCount > 0 ? ` (${pendingCount} несинхронизировано).` : '.'}
        </div>
      ) : null}

      {data.error !== null ? (
        <div className="rounded-2xl border border-tg-danger/40 bg-tg-surface px-4 py-3 text-sm text-tg-text">
          <p className="font-medium">Ошибка загрузки архива</p>
          <p className="mt-1 text-tg-text-secondary">{data.error}</p>
          <div className="mt-2">
            <Button type="button" variant="secondary" onClick={() => void refreshTasks()}>
              Повторить
            </Button>
          </div>
        </div>
      ) : null}

      <TaskFiltersBar filters={filters} onChange={setFilters} allTags={tags} allProjects={projects} />

      {selectionMode && selectedIds.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-tg-accent/40 bg-tg-accent-soft px-3 py-2">
          <span className="text-sm text-tg-accent">Выбрано: {selectedIds.size}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              void handleRestore(selectedArray);
            }}
          >
            Восстановить
          </Button>
          <Button type="button" variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
            Удалить навсегда
          </Button>
          <Button type="button" variant="ghost" onClick={clearSelection}>
            Снять выбор
          </Button>
        </div>
      ) : null}

      {loading ? <ListSkeleton rows={5} /> : null}

      {!loading && data.error === null && items.length === 0 ? (
        <EmptyState
          title={
            hasActiveFilters(filters)
              ? 'По фильтрам ничего не найдено'
              : 'Архив пуст'
          }
          description={
            hasActiveFilters(filters)
              ? 'Попробуйте сбросить фильтры.'
              : 'Задачи, отправленные в архив, появятся здесь.'
          }
          action={
            hasActiveFilters(filters) ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setFilters(EMPTY_TASK_FILTERS);
                }}
              >
                Сбросить фильтры
              </Button>
            ) : null
          }
        />
      ) : null}

      {!loading && items.length > 0 ? (
        <ul className="divide-y divide-tg-border-soft/50 overflow-hidden rounded-2xl border border-tg-border-soft bg-tg-surface">
          {items.map((item) => (
            <li key={item.task.id}>
              <TaskRow
                item={item}
                isDesktop={isDesktop}
                selectionMode={selectionMode}
                selected={selectedIds.has(item.task.id)}
                onSelectToggle={() => {
                  toggleSelect(item.task.id);
                }}
                onOpen={() => {
                  if (selectionMode) {
                    toggleSelect(item.task.id);
                    return;
                  }
                  openTaskEditor(item.task.id);
                }}
                onToggleComplete={() => {
                  void actions.toggleTaskCompleted(item);
                }}
                onRestore={() => {
                  void handleRestore([item.task.id]);
                }}
                onDeleteForever={() => {
                  setDeleteOne({ id: item.task.id, title: item.task.title });
                }}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Удалить навсегда?"
        message={
          selectedIds.size === 1
            ? 'Задача будет удалена без возможности восстановления.'
            : `Будут удалены задач: ${selectedIds.size}. Действие нельзя отменить.`
        }
        confirmLabel="Удалить навсегда"
        danger
        onCancel={() => {
          setDeleteConfirmOpen(false);
        }}
        onConfirm={() => {
          void handleDeleteForever(selectedArray);
        }}
      />

      <ConfirmDialog
        open={deleteOne !== null}
        title="Удалить навсегда?"
        message={
          deleteOne === null
            ? ''
            : `Задача «${deleteOne.title}» будет удалена без возможности восстановления.`
        }
        confirmLabel="Удалить навсегда"
        danger
        onCancel={() => {
          setDeleteOne(null);
        }}
        onConfirm={() => {
          const target = deleteOne;
          if (target === null) {
            return;
          }
          void handleDeleteForever([target.id]);
        }}
      />
    </div>
  );
}
